import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Sparkles, User, Mail, KeyRound, ArrowLeft, Coins, ShieldCheck, Globe, Phone } from "lucide-react";
import { openRazorpayCheckout, fmtMoney, COUNTRIES } from "@/lib/razorpay";
import CouponField from "@/components/CouponField";

export default function Signup() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [country, setCountry] = useState("IN");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paymentOffer, setPaymentOffer] = useState(null);
  const [paying, setPaying] = useState(false);
  const [coupon, setCoupon] = useState(null);

  const startPayment = () => {
    if (!paymentOffer) return;
    setPaying(true);
    openRazorpayCheckout({
      order: { ...paymentOffer.payment, user_id: paymentOffer.user_id },
      prefill: { name: paymentOffer.name, email: paymentOffer.email, contact: paymentOffer.whatsapp },
      onVerified: () => {
        toast.success("Payment successful! Your account is active — sign in to start.");
        navigate("/login?paid=1", { replace: true });
      },
      onDismiss: () => setPaying(false),
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    const waDigits = whatsapp.replace(/\D/g, "").replace(/^(91|0)(?=\d{10}$)/, "");
    if (!/^[6-9]\d{9}$/.test(waDigits)) { toast.error("Please enter a valid 10-digit WhatsApp number"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/signup", {
        name: name.trim(),
        email: email.trim(),
        password,
        whatsapp_number: waDigits,
        parent_email: parentEmail.trim() || undefined,
        country,
        coupon_code: coupon?.code,
      });
      if (data.payment) {
        // Account created — now show the payment step (fee is only revealed here)
        setPaymentOffer({ user_id: data.user_id, payment: data.payment, name: name.trim(), email: email.trim(), whatsapp: waDigits });
      } else {
        toast.success("Account created! Your teacher will approve it shortly.");
        navigate("/login?just-signed-up=1", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-canvas">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-sky">
        <div className="absolute inset-0 opacity-90"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,209,102,0.25) 0, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,90,95,0.35) 0, transparent 45%)" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <Logo size="lg" className="[&_.text-ink]:text-white" />
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-sm font-bold mb-6">
              <Sparkles className="w-4 h-4" /> Join MathWhiz Beads
            </div>
            <h1 className="font-heading text-5xl font-bold leading-tight mb-4">
              Start your abacus<br />adventure.
            </h1>
            <p className="text-lg text-white/90 max-w-md">
              Create an account in seconds. A teacher will review it and set your starting level.
            </p>
          </div>
          <div className="text-sm text-white/70">© {new Date().getFullYear()} MathWhiz Beads</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo size="md" /></div>

          <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-2 text-sky-hover font-bold text-sm mb-4 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
          <h2 className="font-heading text-4xl font-bold text-ink mb-2">{paymentOffer ? "Account created!" : "Create account"}</h2>
          <p className="text-stone mb-8">{paymentOffer ? "One last step — pay your first month's membership fee to activate your account instantly." : "Sign up in seconds and start your abacus adventure."}</p>

          {paymentOffer ? (
            <div data-testid="signup-payment-step" className="p-6 bg-white rounded-3xl card-tactile border-2 border-lemon">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-lemon/50 text-ink flex items-center justify-center shrink-0">
                  <Coins className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-stone">
                    {paymentOffer.payment.auto_debit ? "Monthly auto-debit membership" : "Monthly membership fee"}
                  </div>
                  <div data-testid="signup-fee-amount" className="font-heading text-3xl font-bold text-watermelon">{fmtMoney(paymentOffer.payment.amount, paymentOffer.payment.currency)}<span className="text-base font-semibold text-stone">/mo</span></div>
                  {paymentOffer.payment.coupon_code && paymentOffer.payment.original_amount > paymentOffer.payment.amount && (
                    <div data-testid="signup-fee-coupon" className="text-xs font-bold text-leaf">
                      <span className="line-through text-stone mr-1.5">{fmtMoney(paymentOffer.payment.original_amount, paymentOffer.payment.currency)}</span>
                      {paymentOffer.payment.coupon_code} applied{paymentOffer.payment.auto_debit ? " · every month" : ""}
                    </div>
                  )}
                </div>
              </div>
              <ul className="text-sm text-stone space-y-2 mb-5">
                {paymentOffer.payment.auto_debit ? (
                  <>
                    <li>· Charges automatically every month via UPI Autopay / e-mandate</li>
                    <li>· Sign one authorization now, no re-payments needed</li>
                    <li className="flex items-center gap-1.5 font-semibold text-ink"><ShieldCheck className="w-4 h-4 text-leaf" /> Cancel anytime — your teacher processes the request</li>
                  </>
                ) : (
                  <>
                    <li>· Pay each month via UPI, card, or netbanking (Razorpay)</li>
                    <li>· Your account activates the moment payment succeeds</li>
                    <li className="flex items-center gap-1.5 font-semibold text-ink"><ShieldCheck className="w-4 h-4 text-leaf" /> Fully refundable within 3 days</li>
                  </>
                )}
              </ul>
              <button
                data-testid="signup-pay-button"
                onClick={startPayment}
                disabled={paying}
                className="w-full h-14 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
              >
                {paying ? "Opening checkout…" : `Pay ${fmtMoney(paymentOffer.payment.amount, paymentOffer.payment.currency)} & Activate`}
              </button>
              <button
                data-testid="signup-pay-later"
                onClick={() => { toast.info("Sign in to start your 1-day free trial."); navigate("/login?just-signed-up=1", { replace: true }); }}
                className="w-full mt-3 py-2 text-sm font-bold text-stone hover:text-ink"
              >
                Start my 1-day free trial instead →
              </button>
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div>
              <label className="block text-sm font-bold text-ink mb-2">Your name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble" />
                <input data-testid="signup-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Aarav Sharma" className="w-full h-12 pl-12 pr-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-medium bg-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble" />
                <input data-testid="signup-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full h-12 pl-12 pr-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-medium bg-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-2">WhatsApp number</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble" />
                <input data-testid="signup-whatsapp-input" required type="tel" inputMode="numeric" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="98765 43210" className="w-full h-12 pl-12 pr-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-medium bg-white" />
              </div>
              <p className="text-xs text-stone mt-1">10-digit Indian mobile number. We'll share class updates here.</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-2">Parent email <span className="text-stone font-normal">(optional)</span></label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble" />
                <input data-testid="signup-parent-email-input" type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} placeholder="parent@example.com" className="w-full h-12 pl-12 pr-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-medium bg-white" />
              </div>
              <p className="text-xs text-stone mt-1">We send monthly progress reports here.</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-2">Country</label>
              <div className="relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble pointer-events-none" />
                <select
                  data-testid="signup-country-select"
                  value={country}
                  onChange={(e) => { setCountry(e.target.value); setCoupon(null); }}
                  className="w-full h-12 pl-12 pr-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-medium bg-white appearance-none cursor-pointer"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-stone mt-1">Your monthly fee is shown in your local currency.</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-2">Password</label>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble" />
                <input data-testid="signup-password-input" required minLength={6} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="w-full h-12 pl-12 pr-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-medium bg-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-2">Confirm password</label>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble" />
                <input data-testid="signup-confirm-input" required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full h-12 pl-12 pr-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-medium bg-white" />
              </div>
            </div>
            <CouponField country={country} applied={coupon} onChange={setCoupon} />

            <button
              data-testid="signup-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full h-14 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
            >
              {submitting ? "Creating…" : "Create account"}
            </button>
          </form>
          )}

          <p className="mt-6 text-sm text-stone text-center">
            Already have an account? <Link to="/login" className="text-sky-hover font-bold hover:underline">Sign in</Link>
          </p>
          <p className="mt-3 text-sm text-stone text-center">
            <Link to="/about" data-testid="signup-about-link" className="text-stone font-bold hover:text-ink hover:underline">← Learn about MathWhiz Beads</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
